/**
 * Audit Trail Filters — category chips, principal (user/group) dropdown,
 * date picker popovers.
 *
 * sl-wmbt.5.2: Original filter bar
 * sl-97oo.2.1: DatePicker popover statt native <Input type="date">
 * sl-97oo (this commit): PrincipalFilterSelect statt reiner User-Dropdown.
 *   Unterstützt Auswahl von Nutzern ODER Gruppen (alle Mitglieder der Gruppe).
 */
import { Popover } from '@affine/component';
import { DatePicker } from '@affine/component/ui/date-picker';
import { useI18n } from '@affine/i18n';
import { CloseIcon } from '@blocksuite/icons/rc';
import dayjs from 'dayjs';
import { useCallback, useState } from 'react';

import * as styles from './audit-filters.css';
import {
  PrincipalFilterSelect,
  type PrincipalSelection,
} from './principal-filter-select';
import { AUDIT_CATEGORIES, type AuditFilter } from './types';

export interface WorkspaceMemberOption {
  id: string;
  email: string | null;
  name: string | null;
  avatarUrl: string | null;
}

export interface WorkspaceGroupOption {
  id: string;
  name: string;
  description?: string | null;
}

interface AuditFiltersProps {
  filter: AuditFilter;
  onFilterChange: (filter: AuditFilter) => void;
  workspaceMembers: WorkspaceMemberOption[];
  workspaceGroups: WorkspaceGroupOption[];
}

// ── Date Picker Button + Popover ────────────────────────────────────────

interface DatePickerFieldProps {
  label: string;
  value: string | undefined;
  onChange: (value: string | undefined) => void;
  testId: string;
}

const DatePickerField = ({
  label,
  value,
  onChange,
  testId,
}: DatePickerFieldProps) => {
  const [open, setOpen] = useState(false);

  const handleDateChange = useCallback(
    (dateStr: string) => {
      onChange(dateStr);
      setOpen(false);
    },
    [onChange]
  );

  const handleClear = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      onChange(undefined);
    },
    [onChange]
  );

  const displayValue = value ? dayjs(value).format('DD.MM.YYYY') : label;

  return (
    <Popover
      open={open}
      onOpenChange={setOpen}
      content={
        <div className={styles.datePickerPopover}>
          <DatePicker value={value} onChange={handleDateChange} />
        </div>
      }
      contentOptions={{
        align: 'start',
        sideOffset: 4,
      }}
    >
      <button
        type="button"
        className={`${styles.dateButton} ${value ? styles.dateButtonActive : ''}`}
        data-testid={testId}
      >
        <span>{displayValue}</span>
        {value && (
          <span
            className={styles.dateClearIcon}
            onClick={handleClear}
            role="button"
            tabIndex={-1}
          >
            <CloseIcon width={12} height={12} />
          </span>
        )}
      </button>
    </Popover>
  );
};

// ── Main Filters ────────────────────────────────────────────────────────

export const AuditFilters = ({
  filter,
  onFilterChange,
  workspaceMembers,
  workspaceGroups,
}: AuditFiltersProps) => {
  const t = useI18n();

  const handleCategoryClick = useCallback(
    (category: string | undefined) => {
      onFilterChange({
        ...filter,
        category: filter.category === category ? undefined : category,
      });
    },
    [filter, onFilterChange]
  );

  const principalValue: PrincipalSelection | undefined = filter.userId
    ? { type: 'user', id: filter.userId }
    : filter.groupId
      ? { type: 'group', id: filter.groupId }
      : undefined;

  const handlePrincipalChange = useCallback(
    (next: PrincipalSelection | undefined) => {
      if (!next) {
        onFilterChange({ ...filter, userId: undefined, groupId: undefined });
        return;
      }
      if (next.type === 'user') {
        onFilterChange({ ...filter, userId: next.id, groupId: undefined });
      } else {
        onFilterChange({ ...filter, userId: undefined, groupId: next.id });
      }
    },
    [filter, onFilterChange]
  );

  const handleDateFromChange = useCallback(
    (value: string | undefined) => {
      onFilterChange({ ...filter, dateFrom: value });
    },
    [filter, onFilterChange]
  );

  const handleDateToChange = useCallback(
    (value: string | undefined) => {
      onFilterChange({ ...filter, dateTo: value });
    },
    [filter, onFilterChange]
  );

  return (
    <div className={styles.filterBar}>
      {/* Category chips */}
      <div className={styles.categoryChips}>
        <button
          type="button"
          className={`${styles.filterChip} ${!filter.category ? styles.filterChipActive : ''}`}
          onClick={() => handleCategoryClick(undefined)}
          data-testid="audit-filter-chip-all"
        >
          {t['com.streamlain.settings.workspace.audit-trail.filter.all']()}
        </button>
        {AUDIT_CATEGORIES.map(cat => (
          <button
            key={cat}
            type="button"
            className={`${styles.filterChip} ${filter.category === cat ? styles.filterChipActive : ''}`}
            onClick={() => handleCategoryClick(cat)}
            data-testid={`audit-filter-chip-${cat}`}
          >
            {t[
              `com.streamlain.settings.workspace.audit-trail.category.${cat}`
            ]()}
          </button>
        ))}
      </div>

      {/* Principal (user/group) + Date range row */}
      <div className={styles.secondaryRow}>
        <PrincipalFilterSelect
          members={workspaceMembers}
          groups={workspaceGroups}
          value={principalValue}
          onChange={handlePrincipalChange}
          triggerClassName={styles.userSelect}
          testId="audit-filter-principal"
        />

        <DatePickerField
          label={t[
            'com.streamlain.settings.workspace.audit-trail.filter.date-from'
          ]()}
          value={filter.dateFrom}
          onChange={handleDateFromChange}
          testId="audit-filter-date-from"
        />

        <DatePickerField
          label={t[
            'com.streamlain.settings.workspace.audit-trail.filter.date-to'
          ]()}
          value={filter.dateTo}
          onChange={handleDateToChange}
          testId="audit-filter-date-to"
        />
      </div>
    </div>
  );
};
